import { useSyncExternalStore } from 'react';
import type { SessionUtilisateur } from '../api/types.js';
import { modeDemonstrationActif } from '../api/demo.js';
const CACHE_PREFIXE = 'artosera-regie-';

export interface EnvironnementSw {
  readonly production: boolean;
  readonly conteneur: ServiceWorkerContainer | undefined;
  readonly demonstration: boolean;
}

const ENVIRONNEMENT: () => EnvironnementSw = () => ({
  production: import.meta.env.PROD,
  conteneur: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  demonstration: modeDemonstrationActif(),
});

const UNE_HEURE_MS = 60 * 60 * 1000;
let enAttente: ServiceWorker | null = null;
let minuterie: ReturnType<typeof setInterval> | null = null;
const abonnes = new Set<() => void>();
function annoncer(): void {
  for (const abonne of abonnes) abonne();
}

/** Le service worker existe tant qu'au moins une galerie de la session l'autorise. */
export async function harmoniserServiceWorker(
  session: SessionUtilisateur,
  env: EnvironnementSw = ENVIRONNEMENT(),
): Promise<'enregistre' | 'retire' | 'inchange'> {
  if (!env.production || env.demonstration || !env.conteneur) return 'inchange';
  const autorise = session.galeries.some((galerie) => galerie.horsLigne != null);
  if (!autorise) {
    const enregistrements = await env.conteneur.getRegistrations();
    for (const enregistrement of enregistrements) await enregistrement.unregister();
    if (typeof caches !== 'undefined') {
      for (const nom of await caches.keys())
        if (nom.startsWith(CACHE_PREFIXE)) await caches.delete(nom);
    }
    if (minuterie) clearInterval(minuterie);
    minuterie = null;
    enAttente = null;
    annoncer();
    return enregistrements.length > 0 ? 'retire' : 'inchange';
  }
  const enregistrement = await env.conteneur.register('/sw.js', { scope: '/' });
  enAttente = enregistrement.waiting;
  annoncer();
  void enregistrement.update().catch(() => undefined);
  enregistrement.addEventListener('updatefound', () => {
    const nouveau = enregistrement.installing;
    nouveau?.addEventListener('statechange', () => {
      if (nouveau.state === 'installed' && env.conteneur?.controller) {
        enAttente = nouveau;
        annoncer();
      }
    });
  });
  minuterie ??= setInterval(
    () => void enregistrement.update().catch(() => undefined),
    UNE_HEURE_MS,
  );
  return 'enregistre';
}

export function useMiseAJourDisponible(): boolean {
  return useSyncExternalStore(
    (rappel) => {
      abonnes.add(rappel);
      return () => abonnes.delete(rappel);
    },
    () => enAttente !== null,
    () => false,
  );
}

/** « Recharger » : le nouveau service worker prend la main, puis la page se recharge. */
export function appliquerMiseAJour(): void {
  if (!enAttente || typeof navigator === 'undefined') return;
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  enAttente.postMessage({ type: 'SAUTER_ATTENTE' });
}
